import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Message } from 'element-react'

import { tagHotjarRecording } from '../../helpers/util'

import { logErrorOnServer } from '../../helpers/serverLogging'
import useRequest from '../../hooks/useRequest'
import useUsedEquipment from '../../hooks/useUsedEquipment'
import ContentLayout from '../../components/ContentLayout/ContentLayout'
import { useHistory } from 'react-router-dom'
import CustomDivider from '../../components/CardInfo/components/CustomDivider'
import { ArrowsExpandIcon, LocationMarkerIcon } from '@heroicons/react/outline'
import EquipmentInformation from './components/EquipmentInformation'
import EquipmentSpecs from './components/EquipmentSpecs'
import EquipmentWarranty from './components/EquipmentWarranty'
import UsedEquipmentRequestAvailabilityForm from './components/UsedEquipmentRequestAvailabilityForm'
import useGlobalLoading from '../../hooks/useGlobalLoading'
import ImagePreviewModal from '../../components/CarouselImages/components/ImagePreviewModal'

const logError = logErrorOnServer('Used Equipment Availability Request')

const UsedEquipmentPage = props => {
    const { match } = props

    const { filters } = useUsedEquipment()
    const [equipment, setEquipment] = useState({})
    const { changeLoading } = useGlobalLoading()
    const { handleGetUsedEquipmentDetails } = useRequest()
    const [accordionOpen, setAccordionOpen] = useState(null)
    const [previewOpen, setPreviewOpen] = useState(false)
    const history = useHistory()

    const onGettedEquipmentDetailsSuccess = response => {
        setEquipment(response?.data?.equipment)
        changeLoading(false)
    }

    const onGettedEquipmentDetailsError = response => {
        changeLoading(false)
        const message = response.data ? response.data.message : 'Request failed'
        Message({
            message: message,
            type: 'error',
            showClose: true,
            duration: 1200,
            customClass: 'el-message--error',
            iconClass: 'el-message__icon el-icon-circle-cross',
        })
        tagHotjarRecording('API Error')
        logError(message)
    }

    useEffect(() => {
        changeLoading(true)
        const equipmentId = match?.params?.equipmentId
        const requestUUID = filters?.requestUUID

        handleGetUsedEquipmentDetails(
            equipmentId,
            requestUUID,
            onGettedEquipmentDetailsSuccess,
            onGettedEquipmentDetailsError,
        )
    }, [])

    const renderHeader = () => {
        return (
            <div className="w-full h-auto flex flex-col items-start justify-start p-4 bg-white border-b border-grayLines gap-1">
                <p
                    role="button"
                    onClick={() => {
                        history?.goBack()
                    }}
                    className="text-textDarkGray text-sm font-medium"
                >
                    {'< Go Back'}
                </p>
                <p className="text-black90 font-medium text-lg">
                    {equipment?.showEquipmentName}
                </p>
            </div>
        )
    }

    const renderEquipmentInfo = () => {
        return (
            <div className="lg:w-auto w-full h-auto flex flex-col items-start justify-start gap-y-4">
                <div className="w-full h-auto flex flex-col items-start justify-start gap-y-2">
                    <h1 className="text-black70 font-medium text-2xl text-left">
                        {equipment?.showEquipmentName || ' '}
                    </h1>
                    <p className="text-textDarkerGray font-normal text-xl">
                        {`$${equipment?.displayPrice}` || ' '}
                    </p>
                    <p className="text-textDarkGray font-normal text-sm">
                        *Price includes freight within a 50 mile radius
                    </p>
                </div>
                <CustomDivider hasMargin={false} />
                <div className="w-full max-w-full flex flex-row items-center justify-start gap-x-2 lg:py-0 py-2">
                    <p className="text-black70 text-sm font-medium">
                        Equipment Location
                    </p>
                    <div className="flex-grow h-auto flex items-center justify-end">
                        <LocationMarkerIcon className="text-yellow w-4 h-4 mr-1" />
                        <p className="text-black70 text-sm font-normal">
                            {equipment?.plantDescription}
                        </p>
                    </div>
                </div>
                <CustomDivider hasMargin={false} />
                <EquipmentInformation
                    setOpen={setAccordionOpen}
                    open={accordionOpen === 'equipmentInformation'}
                    equipment={equipment}
                />
                <EquipmentSpecs
                    setOpen={setAccordionOpen}
                    open={accordionOpen === 'specs'}
                    equipment={equipment}
                />
                <EquipmentWarranty
                    setOpen={setAccordionOpen}
                    open={accordionOpen === 'warranty'}
                    equipment={equipment}
                />

                <UsedEquipmentRequestAvailabilityForm equipment={equipment} />
            </div>
        )
    }

    const renderEquipmentImage = () => {
        return (
            <div className="w-full h-auto flex flex-col items-start justify-between gap-y-5">
                <div className="w-full h-auto flex-grow relative pb-3/4">
                    <button
                        onClick={() => setPreviewOpen(true)}
                        className="flex items-center cursor-pointer justify-center bg-white rounded-lg p-2  absolute bottom-4 left-4 z-50"
                    >
                        <ArrowsExpandIcon className="stroke-2 stroke-current mr-0 m-0 text-textDarkerGray w-6 h-6" />
                    </button>

                    <img
                        src={equipment?.image}
                        onError={({ currentTarget }) => {
                            currentTarget.onerror = null
                            currentTarget.src = equipment?.missingImage
                        }}
                        className="absolute object-cover w-full rounded-2xl h-full z-30"
                    />
                </div>
                <p className="text-black70 text-sm font-medium">
                    **Representative photo of equipment condition
                </p>
            </div>
        )
    }

    const renderContentInfo = () => {
        return (
            <div className="w-full h-auto max-h-full flex-grow overflow-auto lg:py-8 lg:px-6 px-4 py-4">
                <div className="w-full h-auto bg-white border border-grayLines lg:p-12 p-4 gap-x-12 gap-y-10 rounded-3xl flex lg:flex-row flex-col items-start justify-between">
                    {renderEquipmentImage()}
                    {renderEquipmentInfo()}
                </div>
            </div>
        )
    }

    return (
        <ContentLayout>
            <ImagePreviewModal
                url={equipment?.image || equipment?.missingImage}
                setIsOpen={setPreviewOpen}
                isOpen={previewOpen}
            />
            {renderHeader()}
            {renderContentInfo()}
        </ContentLayout>
    )
}

UsedEquipmentPage.propTypes = {
    location: PropTypes.shape({
        search: PropTypes.string,
        state: PropTypes.shape({
            source: PropTypes.string,
        }),
    }),
    match: PropTypes.shape({
        params: PropTypes.shape({
            equipmentId: PropTypes.string,
        }),
    }),
    history: PropTypes.shape({
        push: PropTypes.func,
    }),
}

export default UsedEquipmentPage
